<template>
  <div class="blackjack-container">
    <!-- Header -->
    <div class="header">
      <h1>Bem-vindo ao jogo 21 (Black Jack)</h1>
      <h2>Faça 21 ou chegue o mais próximo possível para ganhar a aposta</h2>
    </div>

    <!-- Resultado -->
    <div class="modal" v-if="message">
      <p class="log">{{ message }}</p>
      <p class="scores">
        {{ fullNames[0] }}: {{ playerTotals[0] }} –
        {{ fullNames[1] }}: {{ playerTotals[1] }} –
        {{ fullNames[2] }}: {{ dealerTotal }}
      </p>
      <button class="btn" @click="closeModal">Fechar</button>
    </div>

    <!-- Tela inicial -->
    <div class="initial-screen" v-if="!gameStarted && !isDealing && !message">
      <div class="bet-area">
        <label>Valor da aposta:</label>
        <div class="styled-select">
          <select v-model.number="betAmount">
            <option v-for="v in betOptions" :key="v" :value="v">{{ v }}</option>
          </select>
        </div>
        <button class="btn" @click="confirmBet" :disabled="betPlaced">
          {{ betPlaced ? `Aposta: ${betAmount}` : 'OK' }}
        </button>
        <span class="balance">
          {{ userName }}: {{ userBalance }} <strong>Zephyr Points</strong>
        </span>
      </div>
      <div class="start-menu">
        <button class="btn" @click="startSoloGame" :disabled="!betPlaced">
          Jogar sozinho
        </button>
      </div>
    </div>

    <!-- Mesa de jogo -->
    <div v-else class="game-area">
      <div class="pot-solo">Valor da aposta: {{ betAmount }}</div>
      <div class="game-content">

        <!-- Mesa -->
        <div class="table-wrapper">
          <div class="table" :style="tableStyle"></div>
          <div v-if="isDealing" class="deal-cards">
            <img
              v-for="idx in 6"
              :key="idx"
              class="deal-card"
              :class="dealClass(idx - 1)"
              :style="{ animationDelay: ((idx - 1) * 0.9) + 's' }"
              :src="backImg"
              alt="carta sendo distribuída"
            />
          </div>
          <template v-else>
            <!-- Oponente (bot) -->
            <div class="hand opponent">
              <div class="card-group">
                <img
                  v-for="(card,i) in playerHands[1]"
                  :key="i"
                  class="card"
                  :src="opponentHidden && i===1 ? backImg : cardImages[card.filename]"
                  alt="carta do oponente"
                />
              </div>
              <div class="player-name">
                <span class="player-indicator opponent"></span>{{ fullNames[1] }}
              </div>
            </div>
            <!-- Dealer (bot) -->
            <div class="hand dealer">
              <div class="card-group">
                <img
                  v-for="(card,i) in dealerHand"
                  :key="i"
                  class="card"
                  :src="dealerHidden && i===1 ? backImg : cardImages[card.filename]"
                  alt="carta do dealer"
                />
              </div>
              <div class="player-name">
                <span class="player-indicator dealer"></span>{{ fullNames[2] }}
              </div>
            </div>
          </template>
        </div>

        <!-- Sua mão -->
        <div class="hand player">
          <div class="card-group">
            <img
              v-for="(card,i) in playerHands[0]"
              :key="i"
              class="card"
              :src="cardImages[card.filename]"
              alt="sua carta"
            />
          </div>
          <div class="player-name">{{ fullNames[0] }}</div>
        </div>

        <!-- Controles -->
        <div class="controls">
          <p v-if="!message && !gameOver && currentPlayer===0">Sua vez</p>
          <p v-else-if="!message && !gameOver">Vez de: {{ fullNames[currentPlayer] }}</p>
          <button class="btn" @click="handleHit"
                  :disabled="currentPlayer!==0 || gameOver || isDealing">
            Comprar
          </button>
          <button class="btn" @click="handleStand"
                  :disabled="currentPlayer!==0 || gameOver || isDealing">
            Parar
          </button>
          <button class="btn" @click="startSoloGame" :disabled="isDealing">
            Novo Jogo
          </button>
          <button class="btn" @click="goToInit">
            Voltar
          </button>
        </div>

        <!-- Totais -->
        <div class="status">
          <p>
            {{ fullNames[0] }}: {{ playerTotals[0] }} –
            {{ fullNames[1] }}: {{ playerTotals[1] }} –
            {{ fullNames[2] }}: {{ dealerTotal }}.
          </p>
        </div>
      </div>

      <!-- Log de IA -->
      <div class="ai-log">
        <h3>Log de IA</h3>
        <ul>
          <li v-for="(e,idx) in aiLog" :key="idx" :class="e.actor">{{ e.text }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import backImg from '@/assets/cassino/cartas/back.png'

import tableImg from '@/assets/cassino/blackjack.png'
import { fetchMe, fetchBetBalance, settleBet } from '@/services/api'

// — carregar cartas —
const cardsContext = require.context('@/assets/cassino/cartas', false, /\.png$/)
const cardImages = {}
cardsContext.keys().forEach(k => {
  const file = k.replace('./','')
  cardImages[file] = cardsContext(k)
})

// — baralho e utilitários —
const suits = ['hearts','diamonds','clubs','spades']
const ranks = [
  { name:'ace', value:1 },{ name:'2', value:2 },{ name:'3', value:3 },
  { name:'4', value:4 },{ name:'5', value:5 },{ name:'6', value:6 },
  { name:'7', value:7 },{ name:'8', value:8 },{ name:'9', value:9 },
  { name:'10',value:10 },{ name:'jack', value:10 },
  { name:'queen', value:10 },{ name:'king', value:10 }
]
const jokers = [{ filename:'red_joker.png', value:11 }]

function createDeck() {
  const deck = []
  suits.forEach(s => ranks.forEach(r =>
    deck.push({ filename:`${r.name}_of_${s}.png`, value:r.value })
  ))
  jokers.forEach(j => deck.push({...j}))
  return deck
}

function shuffle(a) {
  for (let i=a.length-1;i>0;i--) {
    const j = Math.floor(Math.random()* (i+1))
    ;[a[i],a[j]] = [a[j],a[i]]
  }
  return a
}

function calculateHandValue(hand) {
  return hand.reduce((sum,c)=> sum + c.value, 0)
}

// — estado reativo —
const deck         = ref([])
const playerHands  = ref([[],[]])
const dealerHand   = ref([])
const dealerHidden = ref(true)
const opponentHidden = ref(true)
const message      = ref('')
const gameStarted  = ref(false)
const gameOver     = ref(false)
const isDealing    = ref(false)
const currentPlayer= ref(0)
const done         = ref([false,false,false])
const aiLog        = ref([])

// — apostas e usuário —
const betOptions   = [100,200,300,500,700,1000]
const betAmount    = ref(100)
const betPlaced    = ref(false)
const userName     = ref('Você')
const userBalance  = ref(0)
const token        = localStorage.getItem('jwt_token')

// — nomes dos participantes —
const fullNames = ['Você','Sabio Babama','Rei Sprite']

// — computeds —
const playerTotals = computed(()=> playerHands.value.map(calculateHandValue))
const dealerTotal  = computed(()=> calculateHandValue(dealerHand.value))
const tableStyle   = computed(()=> ({
  backgroundImage: `url(${tableImg})`
}))

// — overlay de ação —
const overlayMessage = ref('')
function showOverlay(msg) {
  overlayMessage.value = msg
  setTimeout(()=> overlayMessage.value = '',1500)
}

// — animação de deal —
function dealClass(idx) {
  const m = idx % 3
  if (m===0) return 'deal-to-player'
  if (m===1) return 'deal-to-opponent'
  return 'deal-to-dealer'
}

// — iniciar solo —
function startSoloGame() {
  isDealing.value = true
  setTimeout(startGame,5500)
}

// — lógica IA —
function aiDecision(total) {
  if (total<=15) return Math.random()<0.75
  if (total<=18) return Math.random()<0.5
  if (total<=20) return Math.random()<0.25
  return false
}
function aiAction(idx) {
  const name = fullNames[idx]
  const hand = idx===1 ? playerHands.value[1] : dealerHand.value
  const total= calculateHandValue(hand)
  const hit  = aiDecision(total)
  showOverlay(`${name} ${hit?'comprando':'parou'}`)
  if (hit) {
    const d= [...deck.value]; const c=d.pop()
    hand.push(c); deck.value=d
    const nv= calculateHandValue(hand)
    aiLog.value.push({actor: idx===1?'opponent':'dealer', text:`${name} comprou (total: ${nv})`})
    if (nv>21) {
      aiLog.value.push({actor: idx===1?'opponent':'dealer', text:`${name} estourou com ${nv}`})
      done.value[idx]=true
    }
    setTimeout(nextTurn,800)
  } else {
    aiLog.value.push({actor: idx===1?'opponent':'dealer', text:`${name} parou com ${total}`})
    done.value[idx]=true
    setTimeout(nextTurn,800)
  }
}

// — ciclo de turnos —
function nextTurn() {
  if (done.value.every(v=>v)) { revealAndSettle(); return }
  let idx= currentPlayer.value
  do { idx=(idx+1)%3 } while(done.value[idx])
  currentPlayer.value = idx
  if(idx!==0) aiAction(idx)
}

// — revelar resultado —
function revealAndSettle() {
  dealerHidden.value   = false
  opponentHidden.value = false
  const totals = [
    playerTotals.value[0],
    playerTotals.value[1],
    dealerTotal.value
  ].map(v=> v<=21?v:-1)
  const best = Math.max(...totals)
  const wi   = totals.indexOf(best)
  const wn   = fullNames[wi]
  const bet  = betAmount.value
  const lost = wi===0?0:bet
  const won  = wi===0?bet:0
  message.value =
    `${wn} venceu com ${best}\n\n`+
    `Valor da aposta: ${bet}\n`+
    `Valor perdido: ${lost}\n`+
    `Valor ganho: ${won}`
  gameOver.value=true
  if(betPlaced.value && token) {
    settleBet(token,bet,wi===0)
      .then(r=> userBalance.value=r.pvalues)
      .catch(err=>alert('Erro na aposta:'+err.message))
  }
}

// — ações do jogador —
function handleHit() {
  if(currentPlayer.value!==0||gameOver.value||isDealing.value) return
  const d=[...deck.value]; const c=d.pop()
  playerHands.value[0].push(c); deck.value=d
  showOverlay('Você comprou')
  if(calculateHandValue(playerHands.value[0])>21) {
    done.value[0]=true
    setTimeout(nextTurn,400)
  }
}
function handleStand() {
  if(currentPlayer.value!==0||gameOver.value||isDealing.value) return
  done.value[0]=true
  showOverlay('Você parou')
  setTimeout(nextTurn,400)
}

// — novo jogo —
function startGame() {
  currentPlayer.value=0
  gameStarted.value=true
  isDealing.value=false
  gameOver.value=false
  message.value=''
  aiLog.value=[]
  done.value=[false,false,false]
  playerHands.value=[[],[]]
  dealerHand.value=[]
  const newDeck= shuffle(createDeck())
  playerHands.value[0]=[newDeck.pop(),newDeck.pop()]
  playerHands.value[1]=[newDeck.pop(),newDeck.pop()]
  dealerHand.value=[newDeck.pop(),newDeck.pop()]
  deck.value=newDeck
}

// — confirmar aposta —
function confirmBet() {
  if(!token) return alert('Faça login para apostar.')
  if(userBalance.value<betAmount.value) return alert('Fundos insuficientes.')
  betPlaced.value=true
}

// — fechar modal e voltar —
function closeModal() {
  message.value=''
  gameOver.value=false
}
function goToInit() {
  gameStarted.value=false
  betPlaced.value=false
  message.value=''
  gameOver.value=false
  isDealing.value=false
  currentPlayer.value=0
  aiLog.value=[]
  done.value=[false,false,false]
}

// — fetch usuário e saldo —
onMounted(async ()=>{
  if(token) {
    try {
      const me= await fetchMe(token)
      userName.value=me.user.mid
      const bal= await fetchBetBalance(token)
      userBalance.value=parseInt(bal.user.pvalues,10)
    } catch(err){
      console.error(err)
    }
  }
})
</script>

<style>
.blackjack-container{display:flex;flex-direction:column;align-items:center;}
.header{ text-align:center;margin:1rem 0;}
.header h1{font-size:2rem;margin:.5rem 0;}
.header h2{font-size:1.25rem;color:#555;}
.initial-screen{ display:flex; flex-direction:column; align-items:center; gap:1.5rem; border:1px solid #ccc; padding:2.5rem; border-radius:8px; box-shadow:0 2px 12px rgba(0,0,0,0.1); background:var(--v-theme-surface); margin:3rem 0; width:720px;}
.bet-area{ display:flex; gap:1rem; align-items:center;}
.balance{ font-weight:bold;}
.start-menu{ display:flex; gap:1rem;}
.modal{ position:fixed; top:50%; left:50%; transform:translate(-50%,-50%); padding:1.5rem 2rem; width:300px; text-align:center; border-radius:8px; box-shadow:0 2px 10px rgba(0,0,0,0.3); background:var(--v-theme-surface); color:var(--v-theme-on-surface); z-index:10;}
.modal .log{ font-weight:bold; margin-bottom:.5rem;}
.modal .scores{ margin-bottom:1rem;}
.game-area{ display:flex; align-items:flex-start; gap:2rem; width:100%; justify-content:center;}
.game-content{ display:flex; flex-direction:column; align-items:center;}
.table-wrapper{ position:relative; width:80vw !important; height:60vh !important;}
.table-wrapper .table{ width:100%; height:100%; background-size:contain !important; background-position:center !important; background-repeat:no-repeat !important;}
.deal-card{ position:absolute; top:50%; left:50%; width:80px; animation-duration:.5s; animation-fill-mode:forwards;}
@keyframes deal-to-player{from{transform:translate(-50%,-50%)} to{transform:translate(-50%,calc(30vh+30px))}}
@keyframes deal-to-opponent{from{transform:translate(-50%,-50%)} to{transform:translate(calc(-40vw-60px),-50%)}}
@keyframes deal-to-dealer{from{transform:translate(-50%,-50%)} to{transform:translate(calc(40vw+60px),-50%)}}
.deal-to-player{animation-name:deal-to-player;}
.deal-to-opponent{animation-name:deal-to-opponent;}
.deal-to-dealer{animation-name:deal-to-dealer;}
.hand.opponent{position:absolute; top:15%; left:-8%; transform:none;}
.hand.dealer{position:absolute; top:15%; right:-8%; transform:none;}
.hand.player{ position:relative; z-index:2; display:flex; flex-direction:column; align-items:center; margin-top:1rem;}
.hand{ display:flex; flex-direction:column; align-items:center; z-index:1;}
.card-group{ display:flex; gap:10px;}
.card{ width:80px; border-radius:5px; box-shadow:0 2px 5px rgba(0,0,0,0.5); background-color:#fff;}
.player-name{ display:flex; align-items:center; margin-top:.5rem; font-weight:bold;}
.player-indicator{ width:12px; height:12px; margin-right:6px; border-radius:2px; display:inline-block;}
.player-indicator.opponent{ background-color:#007bff;}
.player-indicator.dealer{ background-color:#dc3545;}
.controls{ display:flex; flex-direction:row; align-items:center; gap:1rem; margin:1rem 0;}
.status{ margin-bottom:1rem;}
.status p{ font-weight:bold;}
.btn{ padding:.75rem 1.5rem; font-size:1rem; border:none; border-radius:4px; background-color:#1976d2; color:#fff; cursor:pointer;}
.btn:disabled{ background-color:#aaa; cursor:not-allowed;}
.ai-log{ position:fixed; bottom:30%; right:1rem; width:240px; max-height:40vh; overflow-y:auto; border:1px solid #ccc; padding:1rem; border-radius:8px; background:var(--v-theme-surface);}
.ai-log h3{ margin-top:0; margin-bottom:.5rem; font-size:1.1rem;}
.ai-log ul{ list-style:none; padding:0; margin:0;}
.ai-log li{ margin-bottom:.5rem; font-size:.95rem;}
.ai-log li.opponent{ background-color:rgba(0,123,255,0.1); padding:0.3rem; border-radius:4px;}
.ai-log li.dealer{ background-color:rgba(220,53,69,0.1); padding:0.3rem; border-radius:4px;}
.styled-select{ position:relative; width:120px; display:inline-block; vertical-align:middle;}
.styled-select select{ width:100%; padding:10px 36px 10px 12px; font-size:1rem; border:1px solid rgba(0,0,0,0.12); border-radius:4px; background-color:var(--v-theme-surface); appearance:none; cursor:pointer; background-image:url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'%3E%3Cpath d='M7 10l5 5 5-5z' fill='rgba(0,0,0,0.54)'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 8px center;}
</style>
